/**
 * Client-side PIX-key-by-type validation (FAV-126 / FAV-172).
 *
 * Mirrors the backend `PixKeys.isValid` (modules/merchant/validation/PixKeys.java)
 * so the wizard and the owner edit form can block an obviously-wrong key before
 * the network roundtrip. The class-level `@ValidPixKey` on the backend DTOs
 * remains the source of truth — this is a UX guard.
 *
 * Type contract mirrors the `PixKeyType` enum:
 *   CPF · CNPJ · EMAIL · PHONE · RANDOM (EVP, a random UUID)
 */
import { isValidCpf, isValidCnpj, onlyDigits } from './cpf';

export const PIX_KEY_TYPE_VALUES = ['CPF', 'CNPJ', 'EMAIL', 'PHONE', 'RANDOM'] as const;
export type PixKeyType = (typeof PIX_KEY_TYPE_VALUES)[number];

// Loose e-mail shape (backend caps the whole key at <= 77 chars for the EMAIL type).
const EMAIL_RE = /^[^@\s]{1,64}@[^@\s]+\.[^@\s]+$/;
const EMAIL_MAX = 77;
// BACEN phone keys are E.164 (+55DDDNUMBER). Accept the natural BR forms the operator types:
// bare DDD+number (10-11 digits, e.g. 24999425374) OR with the 55 country code (+55 / 55 + 10-11).
// The backend normalizes to +55DDDNUMBER before persisting (PixKeys.normalize).
const PHONE_RE = /^\+?(?:55)?\d{10,11}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Returns `true` when `key` is a syntactically valid PIX key for `type`.
 * A blank key returns `false` (the "optional" decision is enforced by the
 * schema, not here — this only judges a non-empty value against its type).
 */
export function isValidPixKey(type: PixKeyType, key: string): boolean {
  const trimmed = key.trim();
  if (trimmed.length === 0) return false;
  switch (type) {
    case 'CPF':
      return isValidCpf(trimmed) && onlyDigits(trimmed).length === 11;
    case 'CNPJ':
      return isValidCnpj(trimmed) && onlyDigits(trimmed).length === 14;
    case 'EMAIL':
      return trimmed.length <= EMAIL_MAX && EMAIL_RE.test(trimmed);
    case 'PHONE':
      return PHONE_RE.test(trimmed.replace(/[\s()-]/g, ''));
    case 'RANDOM':
      return UUID_RE.test(trimmed);
    default:
      return false;
  }
}
